import { GCameraShakeController } from "../../../animationController";
import { GAudioEngine, SoundSample } from "../../../core/audioEngine";
import { GUserInputDesc } from "../../../core/input";
import { GRenderTargets } from "../../../core/renderTargets";
import { BindRenderTarget } from "../../../core/resourcesUtils";
import { GScreenDesc } from "../../../core/screenDesc";
import { GTexturePool } from "../../../core/texturePool";
import { ConvertHexColorToVec3, GetVec2, GetVec3, GStack } from "../../../core/types";
import { GTime, MathClamp, MathHashf, MathImpulse, MathLerp, MathSmoothstep } from "../../../core/utils";
import { GSimpleShapesRenderer } from "../../../helpers/shapeRender";
import { RCombinerPass } from "../../../postprocess";
import { GPostProcessPasses } from "../../../renderingMain";
import { CImpactComponentBase } from "../../../tools/tools";
import { GVectorFieldInjector, VectorField } from "../../../vectorField";
import { GSceneDesc, SceneBase } from "../../scene";
import { GHallwaySceneCSS } from "../hallway/hallwayCSS";
import { PayneSceneHallway } from "../hallway/hallwayMain";
import { PayneSceneLightSwitch } from "../lightSwitch/lightSwitch";
import { GBulletSystem } from "./bullets";
import { EPaintingState, PayneSceneDestrPainting } from "./destrPaintingMain";

export function DestrPaintingCopyCommonMeshes(
    exportScene: PayneSceneLightSwitch,
    importScene: PayneSceneDestrPainting,
) {
    importScene.MeshDestrPaniting = exportScene.MeshDestrPainting;

    importScene.AllMeshesArr.push(exportScene.MeshDestrPainting[0]);
    importScene.AllMeshesArr.push(exportScene.MeshDestrPainting[1]);

    importScene.LampLeftAABB = exportScene.LampLeftAABB;
    importScene.LampLeftAABB.Position.y += 0.5;
    importScene.LampRightAABB = exportScene.LampRightAABB;
    importScene.LampRightAABB.Position.y += 0.5;
    importScene.PaintingAABB = exportScene.PaintingAABB;
    importScene.PaintingFrameAABB = exportScene.PaintingFrameAABB;
    importScene.PaintingFrameAABB.Extent.Mul3(0.9, 0.9, 1.5);

    importScene.MeshLampLeftArr = exportScene.MeshLampLeftArr;
    importScene.MeshLampRightArr = exportScene.MeshLampRightArr;

    for (let i = 0; i < 4; i++) {
        importScene.AllMeshesArr.push(exportScene.MeshLampLeftArr[i]);
        importScene.AllMeshesArr.push(exportScene.MeshLampRightArr[i]);
    }

    const b = 0.7;
    importScene.MeshLampRightArr[2].Material.Brightness = b;
    importScene.MeshLampRightArr[3].Material.Brightness = b;
    importScene.MeshLampLeftArr[2].Material.Brightness = b;
    importScene.MeshLampLeftArr[3].Material.Brightness = b;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function TransitionSceneToDestrPaintState(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
    //Desc
    {
        RCombinerPass.BloomStrength = 1.0;
        GPostProcessPasses.Bloom!.Threshold = 0.5;
        //GPostProcessPasses.Bloom!.PreBrightness = 0.5;

        RCombinerPass.DispersionOffsetScale = 1.0;

        RCombinerPass.Exposure = 1.2;

        RCombinerPass.LensFlareScale = 0.5;
        RCombinerPass.GradParamMin = 0.5;

        RCombinerPass.ColorContrast = 1.0;

        const buf = SceneBase.UniformBuffer.Params;

        buf.OccluderPlaneCenter.z = -0.3;

        buf.FresnelMin.x = 0.1;

        buf.EnvMapRotationAngle.x = MathLerp(5.0, 5.35, Math.random());

        ConvertHexColorToVec3("#fff6cc", buf.LightColor);
        ConvertHexColorToVec3("#fff6cc", buf.LightColor2);
        ConvertHexColorToVec3("#fff6cc", buf.EmmissiveColor);
        buf.LightIntensity1.x = 10;
        buf.LampLightIntensity.x = 0.35;
        buf.LampLightRadius.x = 10.0;

        buf.PointLightRadius1.x = 15.0;

        buf.AmbientLight.x = -0.15;

        buf.SpotlightPos2.Set3(0.0, 8.0, -1.0);
        buf.SpotlightTargetPos2.Set3(0.0, 0.0, 0.0);

        buf.SDFHelperFloat1.x = 1.0;
        buf.SDFHelperFloat2.x = 1.0;

        buf.SpotlightPos.z = -18;
        buf.SpotlightPos.y = 1.1;

        if (PayneSceneLightSwitch.GInstance!.SoundHeartbeat.Gain) {
            PayneSceneLightSwitch.GInstance!.SoundHeartbeat.Gain.gain.value = 0.5;
        }
    }

    GHallwaySceneCSS.SetNewPage(3);
}

export function DestrPaintOnUpdateScript(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
    const buf = SceneBase.UniformBuffer.Params;
    const audioContext = GAudioEngine.Instance!.Context;
    const impulseStart = 0.0;
    let impulseMaxDur = 0.5;
    if (scene.TimeSinceFirstRender > impulseStart && scene.TimeSinceFirstRender < impulseStart + impulseMaxDur) {
        const t = scene.TimeSinceFirstRender - impulseStart;
        RCombinerPass.FisheyeParam = MathImpulse(t, 7.0) * 0.3;
    } else {
        RCombinerPass.FisheyeParam = 0.0;
    }

    const flareDur = 2.0;
    if (scene.TimeSinceFirstRender > impulseStart && scene.TimeSinceFirstRender <= impulseStart + flareDur) {
        const t = (scene.TimeSinceFirstRender - impulseStart) / flareDur;
        GPostProcessPasses.Bloom!.Threshold = MathLerp(0.05, 0.5, t);
        RCombinerPass.GradParamMin = MathLerp(1.0, 0.5, t * t);
        //RCombinerPass.Exposure = MathLerp(0.9, 1.2, t);
    }

    const titleStart = 1.0;
    const titleDur = 3.0;
    if (scene.TimeSinceFirstRender > titleStart && scene.TimeSinceFirstRender <= titleStart + titleDur) {
        PayneSceneDestrPainting.setMainTitleSubVisibility(true);
        PayneSceneDestrPainting.setHealthbarVisibility(true);

        const hallway = PayneSceneHallway.GInstance!;
        if (audioContext) {
            if (scene.TimeSinceFirstRender < titleStart + 1.0) {
                if (!hallway.SoundImpactLow.bIsPlaying) {
                    hallway.SoundImpactLow.Play(audioContext);
                }
            }
        }

        if (scene.bBackFilterEnableFromStart) {
            scene.SoundEnableBackFilter();
        }
    } else {
        PayneSceneDestrPainting.setMainTitleSubVisibility(false);
    }

    //Lamp flicker
    if (scene.TimeSinceFirstRender < 3 || scene.PaintState == EPaintingState.AfterDeath) {
        let lampBr = 1.0;
        if (scene.TimeSinceFirstRender < 2 || scene.PaintState == EPaintingState.AfterDeath) {
            let rndHash = MathHashf((GTime.Cur * 0.005) % 10.0);
            let rnd = (Math.sin(GTime.Cur) + 1.0) * 0.5;
            const flickThres = 0.06;
            if (!(rnd > 0.7 - flickThres && rnd < 0.7)) {
                rndHash = 1.0;
            }
            rnd = Math.max(0.85, rnd);
            rndHash = Math.max(0.5, rndHash);
            rnd *= rndHash;

            lampBr = rnd;
        }

        scene.MeshLampRightArr[2].Material.Brightness = 0.7 * lampBr;
        scene.MeshLampRightArr[3].Material.Brightness = 0.7 * lampBr;

        scene.MeshLampLeftArr[2].Material.Brightness = 0.7 * lampBr;
        scene.MeshLampLeftArr[3].Material.Brightness = 0.7 * lampBr;

        if (scene.PaintState == EPaintingState.AfterDeath) {
            SceneBase.UniformBuffer.Params.LightIntensity1.x = 2 * lampBr;
        } else {
            SceneBase.UniformBuffer.Params.LampFlickerScale.x = lampBr;
            SceneBase.UniformBuffer.Params.LightIntensity1.x = 10 * lampBr;
        }
    }

    scene.TimeSinceFirstRender += GTime.Delta;

    {
        if (audioContext) {
            const bulletSystem = GBulletSystem.GInstance!;

            //const bShoot = bulletSystem.bSpawnParticlesThisFrame;
            const bShoot = bulletSystem.bSpawnParticlesThisFrame && scene.PaintState != EPaintingState.AfterDeath;
            //const bShoot = GUserInputDesc.bPointerInputPressedCurFrame && !GUserInputDesc.bPointerInputPressedPrevFrame;
            if (bShoot) {
                scene.SoundIngram.PlayLoopRepeat(audioContext, 0.01, GTime.DeltaTimeScale);
                if (!scene.SoundIngram.bIsPlaying) {
                }
            } else {
                //scene.SoundIngram.Stop();
            }

            if (scene.bBackFilterEnabled && scene.SoundBackFilter) {
                scene.SoundBackFilter.frequency.value = Math.max(
                    scene.bBackFilterEnableFromStart ? 2000 : 400,
                    scene.SoundBackFilter.frequency.value - GTime.Delta * 10000 * 1.0,
                );

                if (!scene.SoundMainTheme0.bIsPlaying && !scene.SoundMainTheme1.bIsPlaying) {
                    if (!(scene.NumTimesMainThemePlayed % 3 == 2)) {
                        let addDelay = 0.0;
                        if (scene.NumTimesMainThemePlayed == 0) {
                            addDelay = 2.25;
                        }
                        scene.SoundMainTheme0.PlayLoopRepeat(
                            audioContext,
                            0.0275 + 0.005 * Math.random(),
                            1.0,
                            addDelay,
                        );
                        scene.NumTimesMainThemePlayed += 1;

                        if (scene.PaintState > EPaintingState.HeartCanShoot) {
                            if (scene.NumTimesMainThemePlayed == 1) {
                                scene.NumTimesMainThemePlayed = 2;
                            }
                        }
                        //console.log(`Main Theme 1 Played ` + scene.NumTimesMainThemePlayed + ` ` + scene.PaintState);
                    } else {
                        scene.SoundMainTheme1.PlayLoopRepeat(audioContext, 0.0275 + 0.005 * Math.random(), 1.0);
                        scene.NumTimesMainThemePlayed += 1;
                    }
                }

                if (scene.SoundMainTheme0.Gain && scene.TimeSinceBackFilterEnabled > 2.0) {
                    scene.SoundMainTheme0.Gain.gain.value = Math.min(
                        0.9,
                        scene.SoundMainTheme0.Gain.gain.value + GTime.Delta * 0.3,
                    );
                }

                scene.TimeSinceBackFilterEnabled += GTime.Delta;
            }

            let SoundToPlay: SoundSample | null = null;

            let addGain = 1.0;
            // eslint-disable-next-line prefer-const
            let addDelay = 0.0;

            if (!GUserInputDesc.bPointerInputPressedCurFrame && bulletSystem.bIntersectionThisFrame) {
                const randomIndex = Math.floor(Math.random() * scene.SoundWoodFallArr.length);
                const hitSound = scene.SoundWoodFallArr[randomIndex];

                if (hitSound) {
                    if (!hitSound.bIsPlaying) {
                        SoundToPlay = hitSound;
                        addGain = 0.2;
                        addDelay = 0.5;
                    }
                }
            }

            if (bulletSystem.bLampIntersectionThisFrame) {
                SoundToPlay = scene.SoundLampHit;
                addGain = 3.0;
            }

            if (bulletSystem.bSceneObjectIntersectionThisFrame && SoundToPlay == null) {
                if (scene.PaintState == EPaintingState.HealProtect) {
                    if (Math.random() < 0.5) {
                        const hitSound = scene.SoundDefaultHitsArr[2];
                        if (!hitSound.bIsPlaying) {
                            SoundToPlay = hitSound;
                        }
                    }

                    if (SoundToPlay == null) {
                        if (Math.random() < 0.2) {
                            const randomIndex = Math.floor(Math.random() * scene.SoundDefaultHitsArr.length);
                            const hitSound = scene.SoundDefaultHitsArr[randomIndex];
                            if (!hitSound.bIsPlaying) {
                                SoundToPlay = hitSound;
                            }
                        }
                    }
                }

                //Sparks
                if (SoundToPlay == null) {
                    if (Math.random() < 0.5) {
                        const randomIndex = Math.floor(Math.random() * scene.SoundPaintSparksArr.length);
                        const hitSound = scene.SoundPaintSparksArr[randomIndex];
                        if (!hitSound.bIsPlaying) {
                            SoundToPlay = hitSound;
                            addGain = 0.03;
                        }
                    }
                }

                //Hit
                if (SoundToPlay == null) {
                    const arrSize = scene.SoundPaintHitsArr.length;
                    // eslint-disable-next-line prefer-const
                    let randomIndex = Math.floor(Math.random() * arrSize);

                    for (let offset = 0; offset < arrSize; offset++) {
                        const i = (randomIndex + offset) % arrSize;
                        const hitSound = scene.SoundPaintHitsArr[i];

                        if (!hitSound.bIsPlaying) {
                            SoundToPlay = hitSound;

                            break;
                        }
                    }
                }

                if (SoundToPlay) {
                    if (scene.PaintState > EPaintingState.HeartCanShoot) {
                        addGain *= 1.5;
                    }
                }
            }

            if (bulletSystem.bIntersectionThisFrame && SoundToPlay == null) {
                if (Math.random() < 0.5) {
                    const hitSound = scene.SoundDefaultHitsArr[2];
                    if (!hitSound.bIsPlaying) {
                        SoundToPlay = hitSound;
                    }
                }

                if (SoundToPlay == null) {
                    if (Math.random() < 0.2) {
                        const randomIndex = Math.floor(Math.random() * scene.SoundDefaultHitsArr.length);
                        const hitSound = scene.SoundDefaultHitsArr[randomIndex];
                        if (!hitSound.bIsPlaying) {
                            SoundToPlay = hitSound;
                        }
                    }
                }

                if (SoundToPlay == null) {
                    //Debris
                    if (Math.random() < 0.1) {
                        const randomIndex = Math.floor(Math.random() * scene.SoundWoodDebrisArr.length);
                        const hitSound = scene.SoundWoodDebrisArr[randomIndex];
                        if (hitSound) {
                            if (!hitSound.bIsPlaying) {
                                SoundToPlay = hitSound;
                            }
                        }

                        /* const arrSize = scene.SoundWoodDebrisArr.length;
                        const randomIndex = Math.floor(Math.random() * arrSize);

                        for (let offset = 0; offset < arrSize; offset++) {
                            const i = (randomIndex + offset) % arrSize; // Wrap around using modulo
                            const hitSound = scene.SoundWoodDebrisArr[i];

                            if (!hitSound.bIsPlaying) {
                                SoundToPlay = hitSound;

                                break;
                            }
                        } */
                    }

                    //Hit
                    if (SoundToPlay == null) {
                        const arrSize = scene.SoundWoodHitsArr.length;
                        const randomIndex = Math.floor(Math.random() * arrSize);

                        for (let offset = 0; offset < arrSize; offset++) {
                            const i = (randomIndex + offset) % arrSize; // Wrap around using modulo
                            const hitSound = scene.SoundWoodHitsArr[i];

                            if (!hitSound.bIsPlaying) {
                                SoundToPlay = hitSound;

                                break;
                            }
                        }
                    }
                }

                /* if (Math.random() < 0.5) {
                    SoundToPlay = null;
                } */
            }

            if (SoundToPlay) {
                if (SoundToPlay.Gain) {
                    SoundToPlay.Gain.gain.value = scene.SoundGain * addGain;
                }

                if (SoundToPlay.Pan) {
                    SoundToPlay.Pan.positionX.setValueAtTime(
                        bulletSystem.IntersectionPos.x * scene.SoundWidth,
                        audioContext.currentTime,
                    );
                    SoundToPlay.Pan.positionY.setValueAtTime(
                        (bulletSystem.IntersectionPos.y - GSceneDesc.Camera.Position.y) * scene.SoundWidth,
                        audioContext.currentTime,
                    );
                    SoundToPlay.Pan.positionZ.setValueAtTime(
                        (bulletSystem.IntersectionPos.z - GSceneDesc.Camera.Position.z) * scene.SoundDepth,
                        audioContext.currentTime,
                    );
                }

                SoundToPlay.PlayLoopRepeat(audioContext, 0.0, GTime.DeltaTimeScale, addDelay);
            }
        }
    }

    if (scene.PaintState == EPaintingState.AfterDeath) {
        const animDur = 30.0;

        impulseMaxDur = 1.5;
        if (scene.TimeAfterPaintDeath > impulseStart && scene.TimeAfterPaintDeath < impulseStart + impulseMaxDur) {
            const t = scene.TimeAfterPaintDeath - impulseStart;
            RCombinerPass.FisheyeParam = MathImpulse(t, 4.0) * 0.3;
        } else {
            RCombinerPass.FisheyeParam = 0.0;
        }

        if (scene.TimeAfterPaintDeath <= animDur) {
            let t = scene.TimeAfterPaintDeath / animDur;
            t = MathSmoothstep(0, 1, t);

            GSceneDesc.Camera.Position.z = MathLerp(-10.5, 14.5, t);
        }

        if (GSceneDesc.Camera.Position.z > 3.5) {
            if (audioContext) {
                scene.SoundsViolinConnectPanner();
            }

            scene.bTVRenderState = true;
            const speed = 0.3;
            scene.TVTransitionParam = Math.max(0.0, scene.TVTransitionParam - GTime.Delta * speed);
            buf.SDFHelperFloat2.x = MathSmoothstep(0.0, 1.0, scene.TVTransitionParam);

            GPostProcessPasses.Bloom!.Threshold = MathLerp(0.2, 0.5, scene.TVTransitionParam);
            GPostProcessPasses.Bloom!.PreBrightness = MathLerp(1.15, 1.0, scene.TVTransitionParam);

            if (scene.SoundTVFilter) {
                scene.SoundTVFilter.frequency.value = MathLerp(2000, 0.0, scene.TVTransitionParam);
                scene.SoundMainTheme0.Gain!.gain.value = MathLerp(0.6, 1.0, scene.TVTransitionParam);
                scene.SoundMainTheme1.Gain!.gain.value = MathLerp(0.6, 1.0, scene.TVTransitionParam);
            }
        }

        if (scene.TVTransitionParam < 0.25) {
            const speed = 0.1;
            scene.TVTransitionParam2 = Math.max(0.0, scene.TVTransitionParam2 - GTime.Delta * speed);
            buf.SDFHelperFloat1.x = MathSmoothstep(0.0, 1.0, scene.TVTransitionParam2);
        }

        if (scene.TVTransitionParam2 < 0.25 && scene.ETVSwitchState < 2) {
            PayneSceneDestrPainting.setMaxSubNum(2);
            PayneSceneDestrPainting.setMaxSubVisibility(true);
            PayneSceneDestrPainting.setAlanSubVisibility(false);
        }

        scene.TimeAfterPaintDeath += GTime.Delta;
    }
}

export class PaintDestrImpactComponent extends CImpactComponentBase {
    LoadResources(gl: WebGL2RenderingContext): void {
        this.LightFlareTexture = GTexturePool.CreateTexture(gl, false, `flare01`);
        this.LightFlareTexture1 = GTexturePool.CreateTexture(gl, false, `burst1`);
        this.LightFlareTexture2 = GTexturePool.CreateTexture(gl, false, `smile4`);
        //this.LightFlareTexture3 = GTexturePool.CreateTexture(gl, false, `flareFlash0`);
    }

    UpdateAndRenderForScene(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
        //const timeSinceStart = GTime.Cur - this.StartTime;
        const timeSinceStart = this.TimeSinceStart;
        let bMainImpactThisFrame = false;

        const buf = SceneBase.UniformBuffer.Params;

        if (timeSinceStart < 30) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.ONE, gl.ONE);

            const colorCur = GStack.TempVec3;
            colorCur.Set3(1.0, 1.0, 1.0);
            colorCur.Mul(0.4);

            //flare 1
            {
                const duration = 2.0;

                if (timeSinceStart < duration) {
                    const t = MathClamp(timeSinceStart / duration, 0.0, 1.0);

                    colorCur.SetSame(0.2);
                    GSimpleShapesRenderer.GInstance!.RenderPoint(
                        gl,
                        this.IntersectionPosWS,
                        t * 10.25,
                        colorCur,
                        false,
                        0.0,
                        this.LightFlareTexture,
                    );

                    /* buf.LightIntensity1.x = MathLerp(10.0, 2.5, Math.sqrt(t));

                    const el = MathLerp(1.0, 0.8, Math.sqrt(t));
                    scene.MeshLampRightArr[2].Material.Brightness = 0.7 * el;
                    scene.MeshLampRightArr[3].Material.Brightness = 0.7 * el;
                    scene.MeshLampLeftArr[2].Material.Brightness = 0.7 * el;
                    scene.MeshLampLeftArr[3].Material.Brightness = 0.7 * el; */
                }
            }

            gl.blendEquation(gl.FUNC_ADD);

            //burst 1
            {
                const start = 2.0;
                const duration = 2.0;

                if (timeSinceStart > start && timeSinceStart < start + duration) {
                    if (!this.bMainImpactTriggered) {
                        this.bMainImpactTriggered = true;
                        bMainImpactThisFrame = true;
                    }

                    const t = MathClamp((timeSinceStart - start) / duration, 0.0, 1.0);

                    colorCur.SetSame(0.2);
                    GSimpleShapesRenderer.GInstance!.RenderPoint(
                        gl,
                        this.IntersectionPosWS,
                        /* Math.sqrt */ t * 70.25,
                        colorCur,
                        false,
                        0.0,
                        this.LightFlareTexture1,
                    );

                    //if (timeSinceStart < start + 0.1) {
                    if (bMainImpactThisFrame) {
                        const ndcPos = GStack.TempVec3_2;
                        ndcPos.Set(this.IntersectionPosWS);
                        ndcPos.Negate(buf.CameraPosition);
                        ndcPos.x *= buf.CameraZoom.x;
                        ndcPos.y *= buf.CameraZoom.x;
                        ndcPos.x /= buf.ScreenRatio.x;
                        ndcPos.x /= 0.1 + ndcPos.z;
                        ndcPos.y /= 0.1 + ndcPos.z;

                        GVectorFieldInjector.GInstance?.InjectImpulse(
                            gl,
                            VectorField.GFullscreenInstance!,
                            GetVec2(ndcPos.x + 0.25 * (-0.5 + Math.random()), ndcPos.y + 0.25 * (-0.5 + Math.random())),
                        );

                        buf.EnvMapRotationAngle.x = 3.42;

                        GCameraShakeController.ShakeCameraFast(0.1 * 7.5);

                        //GPostProcessPasses.Bloom!.Threshold = 0.3;
                    }
                }
            }

            if (bMainImpactThisFrame) {
                VectorField.GFullscreenInstance!.BindCurDensityRT(gl);

                const bright = 0.5;
                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    GetVec3(0.0, 2.5, 0.0),
                    GetVec3(2.0, 2.0, 0),
                    GetVec3(bright, bright, bright),
                    false,
                    undefined,
                    this.LightFlareTexture2,
                    true,
                );

                PayneSceneDestrPainting.setHealthbarVisibility(false);

                scene.PaintState = EPaintingState.AfterDeath;

                const hallway = PayneSceneHallway.GInstance!;
                const audioContext = GAudioEngine.Instance!.Context;
                if (audioContext) {
                    hallway.SoundImpactLow.Play(audioContext);
                    scene.SoundLampHit.PlayLoopRepeat(audioContext, 0.0, 0.8);
                }

                scene.StopSynthSound();

                PayneSceneDestrPainting.setMaxSubVisibility(true);

                const subDur = 7.0;
                setTimeout(() => {
                    PayneSceneDestrPainting.setMaxSubNum(1);

                    setTimeout(() => {
                        PayneSceneDestrPainting.setMaxSubVisibility(false);
                    }, subDur * 1000);
                }, subDur * 1000);

                scene.HeartMesh[0].Material.bisEmmiter = false;
                scene.HeartMesh[1].Material.bisEmmiter = false;
            }

            if (bMainImpactThisFrame) {
                SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(this.IntersectionPosWS);
                SceneBase.UploadSerializedUniformBufferToGPU(gl);
            }

            scene.ImpactSmokeParticles.OnUpdate(gl, scene.WallTiles, 9, bMainImpactThisFrame);
            scene.ImpactSparksParticles.OnUpdate(gl, scene.WallTiles, 9, bMainImpactThisFrame);

            gl.disable(gl.BLEND);

            {
                BindRenderTarget(
                    gl,
                    GRenderTargets.SceneColorFramebuffer!,
                    GScreenDesc.RenderTargetSize,
                    false,
                    false,
                    false,
                );

                scene.ImpactSparksParticles.Render(gl, 4);
            }
        }

        //
        this.TimeSinceStart += GTime.Delta * GTime.DeltaTimeScale;
    }
}
